import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom'
import { T } from '@/lib/theme'
import { TopBar } from '@/components/TopBar'
import { Footer } from '@/components/Footer'
import SkyBackground from '@/components/SkyBackground'
import { ErrorBoundary } from '@/components/ErrorBoundary'
import { ReferralCapture } from '@/components/ReferralCapture'
import { TestnetFaucet } from '@/components/TestnetFaucet'
import { BuyPage } from '@/pages/BuyPage'
import { EarnPage } from '@/pages/EarnPage'
import { MarketsPage } from '@/pages/MarketsPage'
import { ReserveDetailPage } from '@/pages/ReserveDetailPage'
import { VaultsPage } from '@/pages/VaultsPage'
import { HUsdPage } from '@/pages/HUsdPage'
import { VaultDetailPage } from '@/pages/VaultDetailPage'
import { StakePage } from '@/pages/StakePage'
import { StatsPage } from '@/pages/StatsPage'
import { BuybackPage } from '@/pages/BuybackPage'
import { AuctionPage } from '@/pages/AuctionPage'
import { PortfolioPage } from '@/pages/PortfolioPage'
import { LeaderboardPage } from '@/pages/LeaderboardPage'
import { MultiplyPage } from '@/pages/MultiplyPage'
import { MultiplyDetailPage } from '@/pages/MultiplyDetailPage'
import { isFeatureEnabled } from '@/lib/features'
import { BuyIcon, EarnIcon, BorrowIcon, MultiplyIcon, VaultsIcon, StakeIcon, DaoIcon, BuybackIcon, PortfolioIcon, LeaderboardIcon, FarmIcon } from '@/components/NavIcons'

const TABS = [
  { id: '/buy',       label: 'Buy',       icon: BuyIcon },
  { id: '/auction',   label: 'Auction',   icon: FarmIcon },
  { id: '/earn',      label: 'Earn',      icon: EarnIcon },
  { id: '/husd',    label: 'hUSD',    icon: EarnIcon },
  { id: '/markets',   label: 'Markets',   icon: BorrowIcon },
  { id: '/multiply',  label: 'Multiply',  icon: MultiplyIcon },
  { id: '/vaults',    label: 'Vaults',    icon: VaultsIcon },
  { id: '/stake',     label: 'Stake',     icon: StakeIcon },
  { id: '/buyback',   label: 'Buyback',   icon: BuybackIcon },
  { id: '/stats',     label: 'Stats',     icon: DaoIcon },
  { id: '/portfolio', label: 'Portfolio', icon: PortfolioIcon },
  { id: '/leaderboard', label: 'Leaderboard', icon: LeaderboardIcon },
]

/// Real routed pages - one URL per section, back to how the app worked before
/// this session's single-scrolling-page merge. TopBar's existing tab-rail
/// component didn't change shape; it's just wired to `useNavigate`/
/// `useLocation` instead of local state now, so a tab click is a real
/// navigation (back/forward, bookmarks, shareable links) rather than a
/// scroll-to-anchor jump.
///
/// No Admin page (CHANGELOG.md item 35) - genesis-only config setters are now
/// coordinated directly rather than through a persistent, genesis-gated web
/// UI, and the permissionless keeper actions that used to live here already
/// have their own public trigger on the page they actually belong to,
/// reachable by anyone, not just genesis on the local chain.
function AppShell() {
  const navigate = useNavigate()
  const location = useLocation()

  // Staged launch: only the enabled features get a nav tab (see src/lib/features.js). A hidden
  // feature's routes below also redirect home, so a stale link or a typed URL cannot reach a page
  // that this wave has not launched yet.
  const visibleTabs = TABS.filter((t) => isFeatureEnabled(t.id.slice(1)))

  return (
    // No minHeight here: #root already fills the viewport (its min-height renders as 100vh, desktop
    // included) and the fixed SkyBackground covers the screen regardless, so the page is exactly as
    // tall as its content and only scrolls when the content overflows. A `100vh` here double-counted
    // under the desktop #root zoom (~125vh) and forced a scrollbar on every page.
    <div style={{ color: T.text, position: 'relative' }}>
      <SkyBackground />
      <div style={{ position: 'relative', zIndex: 1 }}>
        <TopBar tab={location.pathname} setTab={navigate} tabs={visibleTabs} />
        <TestnetFaucet />
        <ReferralCapture />
        <main>
          <ErrorBoundary>
            <Routes>
              {/* Home page retired (2026-08-29) — Markets is the default landing until a new home
                  page is built. Redirect keeps one canonical /markets URL (nav highlight, deep links). */}
              <Route path="/" element={<Navigate to="/markets" replace />} />
              <Route path="/buy" element={<BuyPage />} />
              <Route path="/earn" element={isFeatureEnabled('earn') ? <EarnPage /> : <Navigate to="/" replace />} />
              <Route path="/husd" element={isFeatureEnabled('husd') ? <HUsdPage /> : <Navigate to="/" replace />} />
              <Route path="/markets" element={<MarketsPage />} />
              {/* One reserve, addressed by its UNDERLYING asset address - the thing the pool
                  itself names. Not the display key: keys are slugs derived from symbols, and two
                  assets can present the same symbol. Mirrors /vaults/:address exactly. */}
              <Route path="/markets/:asset" element={<ReserveDetailPage />} />
              <Route path="/multiply" element={isFeatureEnabled('multiply') ? <MultiplyPage /> : <Navigate to="/" replace />} />
              {/* Addressed by the LOOPS entry id (e.g. wsteth-eth) rather than by an address: a
                  loop is a pair plus a strategy, and no single contract address names it. */}
              <Route path="/multiply/:id" element={isFeatureEnabled('multiply') ? <MultiplyDetailPage /> : <Navigate to="/" replace />} />
              <Route path="/vaults" element={<VaultsPage />} />
              <Route path="/vaults/:address" element={<VaultDetailPage />} />
              <Route path="/stake" element={<StakePage />} />
              {/* Auction is always visible; AuctionSection greys the form until the input oracle is live. */}
              <Route path="/auction" element={<AuctionPage />} />
              <Route path="/mining" element={<Navigate to="/auction" replace />} />
              <Route path="/buyback" element={<BuybackPage />} />
              <Route path="/stats" element={<StatsPage />} />
              <Route path="/portfolio" element={<PortfolioPage />} />
              <Route path="/leaderboard" element={<LeaderboardPage />} />
              {/* Naming history: this overview page was "Stats", briefly "DAO", now "Stats" again
                  (DAO implied a governance surface that does not exist). Buyback was folded in here
                  during the merge, now split back out to its own /buyback page above. Liquidations
                  still lives inside Stats. Redirect every old link/bookmark. */}
              <Route path="/dao" element={<Navigate to="/stats" replace />} />
              <Route path="/liquidations" element={<Navigate to="/stats" replace />} />
              {/* Borrow renamed to Markets: the pool is symmetric, so that page supplies AND
                  borrows every reserve - "Borrow" described half of it. "Markets" is also the
                  term Aave/Compound users already scan for, the same inherit-the-vocabulary
                  argument behind the ERC-4626 and Aave-shaped view surfaces. */}
              <Route path="/borrow" element={<Navigate to="/markets" replace />} />
              {/* Mint merged into Markets (minting only ever exists to feed its collateral
                  form) - redirect old links/bookmarks. */}
              <Route path="/mint" element={<Navigate to="/markets" replace />} />
              {/* Farm retired: HORIZON/USDx is a ALM vault like any other now, so the Vaults page
                  is where that product lives. See CHANGELOG.md. */}
              <Route path="/farm" element={<Navigate to="/vaults" replace />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </ErrorBoundary>
        </main>
        <Footer />
      </div>
    </div>
  )
}

export default function App() {
  return (
    <BrowserRouter>
      <AppShell />
    </BrowserRouter>
  )
}
